/**
 * ScheduleTestBlock — the confirmation card for a `schedule_test` proposal.
 *
 * The assistant only DRAFTS (backend tool propose_test_schedule). Nothing is
 * scheduled until the HR clicks "Schedule" here; the card then calls the very
 * same endpoint the page's scheduler uses (POST /api/schedule_test_jobId,
 * form-encoded, one request for all candidates), so authorization, plan gating
 * and credit charging are identical to manual scheduling. Candidate emails are
 * looked up from the page's own candidate list (never from the model).
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, Check, Loader2, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/config/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { useCopilot } from './CopilotContext';
import { InitialsAvatar, StatusPill } from './CopilotBlocks';

export const SCHEDULE_ENDPOINT = '/api/schedule_test_jobId';

function fmtWhen(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Build the form body exactly like InterviewSchedulePopup does. */
export function buildScheduleBody({ jobId, testType, scheduledTime, emails, topic, difficulty, language, companyId, employerEmail }) {
  const body = new URLSearchParams({
    jobId: String(jobId ?? ''),
    scheduledTime: scheduledTime,
    selected_users: emails.join(','),
    topic: topic || '',
    companyId: companyId || '',
    test_type: testType,
    employer_email: employerEmail || '',
    test_platform: 'web',
    difficulty: difficulty || 'medium',
    screening_topics: JSON.stringify([]),
    coding_topics: JSON.stringify([]),
    personalized_coding_questions: JSON.stringify([]),
  });
  if (language) body.append('language', language);
  if (testType.startsWith('/realtime-')) {
    body.append('mode', 'realtime');
    body.append('is_realtime', '1');
  }
  if (testType === '/aptitude') {
    body.append('aptitude_config', JSON.stringify({}));
    body.append('custom_aptitude_questions', JSON.stringify([]));
  }
  return body;
}

export default function ScheduleTestBlock({ block, readOnly = false, onPrompt }) {
  const copilot = useCopilot();
  const [when, setWhen] = useState(block.scheduled_time || '');
  const [difficulty, setDifficulty] = useState(block.difficulty || 'medium');
  const [language, setLanguage] = useState(block.language || '');
  const [state, setState] = useState('idle'); // idle | busy | done | error | dismissed
  const [result, setResult] = useState(null);

  const candidates = Array.isArray(block.candidates) ? block.candidates : [];
  const warnings = Array.isArray(block.warnings) ? block.warnings : [];
  const needsLanguage = Boolean(block.needs_language);
  const languageOptions = Array.isArray(block.language_options) && block.language_options.length
    ? block.language_options
    : ['Python', 'JavaScript', 'NodeJS', 'C++', 'C', 'Java'];
  const difficultyOptions = Array.isArray(block.difficulty_options) && block.difficulty_options.length
    ? block.difficulty_options
    : ['easy', 'medium', 'hard'];

  // Emails come from the page's candidate list, keyed by application id.
  const emailById = useMemo(() => {
    const map = new Map();
    (copilot.candidates || []).forEach((c) => {
      const id = c.application_id ?? c.id;
      const email = c.candidate_email || c.email;
      if (id != null && email) map.set(Number(id), email);
    });
    return map;
  }, [copilot.candidates]);
  const missingEmails = candidates.filter((c) => !emailById.has(Number(c.application_id)));

  const canSchedule =
    !readOnly && state === 'idle' && candidates.length > 0 && !!when && (!needsLanguage || !!language) && missingEmails.length === 0;

  const handleSchedule = async () => {
    if (!canSchedule) return;
    const whenDate = new Date(when);
    if (Number.isNaN(whenDate.getTime()) || whenDate.getTime() < Date.now()) {
      toast.error('Pick a future date and time.');
      return;
    }
    setState('busy');
    try {
      let companyId = '';
      let employerEmail = '';
      try {
        companyId = window.localStorage.getItem('companyAccountId') || '';
        employerEmail =
          window.sessionStorage.getItem('employer_email') || window.localStorage.getItem('employer_email') || '';
      } catch {
        /* storage unavailable */
      }
      const body = buildScheduleBody({
        jobId: block.job_id ?? copilot.jobId,
        testType: block.test_type,
        scheduledTime: when,
        emails: candidates.map((c) => emailById.get(Number(c.application_id))),
        topic: copilot.jobDetails?.job_title || '',
        difficulty,
        language: needsLanguage ? language : '',
        companyId,
        employerEmail,
      });
      const res = await fetch(`${API_BASE_URL}${SCHEDULE_ENDPOINT}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      let info = null;
      try {
        info = await res.clone().json();
      } catch {
        /* not JSON */
      }
      if (!res.ok) {
        let message = info?.error || `Scheduling failed (${res.status})`;
        if (res.status === 403 && info?.upgrade_required) message = `${block.label} isn't included in your plan.`;
        if (res.status === 402) message = `Not enough credits for ${block.label}.`;
        throw new Error(message);
      }
      setResult(info || {});
      setState('done');
      toast.success(`${block.label} scheduled for ${candidates.length} candidate${candidates.length === 1 ? '' : 's'}.`);
      if (typeof copilot.refreshCandidates === 'function') copilot.refreshCandidates();
      if (onPrompt) {
        onPrompt(
          `Scheduled ${block.label} for ${candidates.map((c) => c.name).join(', ')} at ${fmtWhen(when)}.`,
          { system: true }
        );
      }
    } catch (err) {
      setResult({ error: err?.message || 'Scheduling failed' });
      setState('error');
      toast.error(err?.message || 'Scheduling failed');
    }
  };

  const header = (
    <div className="flex items-start gap-2">
      <span className="cp-orb mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center" aria-hidden="true">
        <CalendarClock className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold leading-5 text-foreground">
          Schedule {block.label || 'test'}
          {block.minutes ? <span className="cp-muted ml-1.5 text-[11px] font-normal">· {block.minutes} min</span> : null}
        </div>
        <div className="cp-muted text-[11px] leading-4">
          {readOnly
            ? 'Proposal from an earlier conversation. Ask again to schedule.'
            : 'Nothing is scheduled until you confirm. Candidates receive the test invitation after that.'}
        </div>
      </div>
    </div>
  );

  return (
    <div className="cp-card space-y-2.5 p-3" data-testid="schedule-test-card" data-state={state}>
      {header}

      {/* Candidates */}
      <div>
        <div className="cp-label mb-1 flex items-center gap-1">
          <Users className="h-3 w-3" aria-hidden="true" /> {candidates.length} candidate{candidates.length === 1 ? '' : 's'}
        </div>
        <ul className="flex flex-wrap gap-1.5">
          {candidates.map((c) => (
            <li key={c.application_id} className="cp-chip cp-chip-static h-7 gap-1.5 pl-1 pr-2" title={c.status || ''}>
              <InitialsAvatar name={c.name} size={20} />
              <span className="max-w-[140px] truncate text-[12px]">{c.name}</span>
              {c.status && <StatusPill status={c.status} className="ml-0.5" />}
            </li>
          ))}
        </ul>
        {missingEmails.length > 0 && (
          <div className="cp-banner cp-banner-warn mt-1.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              {missingEmails.map((c) => c.name).join(', ')} {missingEmails.length === 1 ? 'is' : 'are'} not on this page's list
              right now; reload the applicants and ask again.
            </span>
          </div>
        )}
      </div>

      {/* Settings */}
      {!readOnly && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="space-y-1 sm:col-span-1">
            <span className="cp-label">When</span>
            {/* Native picker: same "yyyy-MM-ddTHH:mm" value the page's scheduler sends. */}
            <input
              type="datetime-local"
              className="cp-focus h-9 w-full rounded-md border border-border bg-background px-2 text-xs"
              value={when}
              min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
              onChange={(e) => setWhen(e.target.value)}
              disabled={state !== 'idle' && state !== 'error'}
              aria-label="Test date and time"
              data-testid="schedule-when"
            />
          </label>
          <label className="space-y-1">
            <span className="cp-label">Difficulty</span>
            <Select value={difficulty} onValueChange={setDifficulty} disabled={state !== 'idle'}>
              <SelectTrigger className="h-9 text-xs" aria-label="Difficulty">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                {difficultyOptions.map((d) => (
                  <SelectItem key={d} value={d} className="text-xs capitalize">
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          {needsLanguage && (
            <label className="space-y-1">
              <span className="cp-label">Language</span>
              <Select value={language} onValueChange={setLanguage} disabled={state !== 'idle'}>
                <SelectTrigger className="h-9 text-xs" aria-label="Coding language">
                  <SelectValue placeholder="Choose language" />
                </SelectTrigger>
                <SelectContent>
                  {languageOptions.map((l) => (
                    <SelectItem key={l} value={l} className="text-xs">
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}
        </div>
      )}
      {readOnly && (
        <div className="cp-muted text-[12px]">
          {when ? fmtWhen(when) : 'No time set'} · {difficulty}
          {needsLanguage && language ? ` · ${language}` : ''}
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <ul className="space-y-1">
          {warnings.map((w, i) => (
            <li key={i} className="cp-banner cp-banner-warn py-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Footer */}
      {!readOnly && (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
          <div className="cp-muted cp-num text-[11px]">
            {block.credits_estimate != null
              ? `Estimated ${block.credits_estimate} credit${block.credits_estimate === 1 ? '' : 's'}`
              : 'Credits as per your plan'}
          </div>
          {state === 'done' ? (
            <span className="cp-pill-ok inline-flex h-8 items-center gap-1 rounded-full px-3 text-[12px] font-medium" data-testid="schedule-done">
              <Check className="h-3.5 w-3.5" aria-hidden="true" /> Scheduled
              {result?.test_id ? <span className="cp-num opacity-70">· #{result.test_id}</span> : null}
            </span>
          ) : state === 'dismissed' ? (
            <span className="cp-muted text-[12px]">Dismissed</span>
          ) : (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                className="cp-chip cp-focus h-8"
                onClick={() => setState('dismissed')}
                disabled={state === 'busy'}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" /> Not now
              </button>
              <button
                type="button"
                className={cn('cp-orb cp-focus inline-flex h-8 items-center gap-1.5 px-3.5 text-[12px] font-semibold', !canSchedule && 'opacity-50')}
                onClick={handleSchedule}
                disabled={!canSchedule && state !== 'error'}
                data-testid="schedule-confirm"
              >
                {state === 'busy' ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                {state === 'error' ? 'Try again' : 'Schedule'}
              </button>
            </div>
          )}
        </div>
      )}
      {state === 'error' && result?.error && (
        <div className="cp-banner cp-banner-bad">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{result.error}</span>
        </div>
      )}
    </div>
  );
}
